import { Box, Button, CircularProgress, MenuItem, Stack, Typography } from '@mui/material'
import { MainApi } from 'api/MainApi'
import { ApiError } from 'api/schema'
import clsx from 'clsx'
import SelectForm from 'components/SelectForm/SelectForm'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import useAllApriorMaps from 'hooks/useAllApriorMaps'
import { ReactComponent as CancelSvg } from 'images/icons/cancel.svg'
import { ReactComponent as SaveSvg } from 'images/icons/save.svg'
import isEqual from 'lodash.isequal'
import ScheduleSettings from 'pages/Mission/MissionView/MissionButtons/MissionSettingsModal/ScheduleSettings/ScheduleSettings'
import VisualSettings from 'pages/Mission/MissionView/MissionButtons/MissionSettingsModal/VisualSettings/VisualSettings'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useMapStore from 'store/map'
import useMissionStore from 'store/mission'
import {
  APRIOR_MAP_STATUS,
  DEFAULT_VIZUALIZATION_SETTINGS,
  MISSION_MAP_DRAWING_MODES,
  MISSION_PAGE_MODES,
} from 'utils/constants'
import { IApriorMapVersion } from 'utils/interfaces'
import { toApriorMapId, toApriorMapVersionId } from 'utils/types'
import { useShallow } from 'zustand/react/shallow'

import style from 'pages/Mission/MissionView/MissionButtons/MissionSettingsModal/MissionSettingsModal.module.css'

interface IDialogProps {
  // isOpen: boolean
  missionId: number
}

const MissionSettingsModal = ({ missionId }: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()
  const {
    setMode,
    setMapMode,
    setMissionName,
    setApriorMapVersionId,
    setVisualSettings,
    visualSettings,
    setTempVisualSettings,
    tempVisualSettings,
    missionInfo,
  } = useMissionStore(
    useShallow(state => ({
      setMode: state.setMode,
      setMapMode: state.setMapMode,
      setMissionName: state.setMissionName,
      setApriorMapVersionId: state.setApriorMapVersionId,
      setVisualSettings: state.setVisualSettings,
      setTempVisualSettings: state.setTempVisualSettings,
      visualSettings: state.visualSettings,
      tempVisualSettings: state.tempVisualSettings,
      missionInfo: state.missionInfo,
    }))
  )
  const [tempMissionName, setTempMissionName] = useState(missionInfo?.name || '')
  const [hasNameError, setNameHasError] = useState(false)
  const tempApriorMapVersionId = useMapStore(s => s.tempApriorMapVersionId)
  const setTempApriorMapVersionId = useMapStore(s => s.setTempApriorMapVersionId)
  const clearTempApriorMapVersionId = useMapStore(s => s.clearTempApriorMapVersionId)
  const selectedApriorMapVersionId =
    tempApriorMapVersionId === undefined ? missionInfo?.apriorMapVersionId ?? null : tempApriorMapVersionId
  const { data: maps = [] } = useAllApriorMaps()
  const [actionInProgress, setActionInProgress] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const selectedMap = useMemo(
    () => maps.find(m => m.versions.some((v: IApriorMapVersion) => v.id === selectedApriorMapVersionId)),
    [maps, selectedApriorMapVersionId]
  )

  useEffect(() => () => clearTempApriorMapVersionId(), [clearTempApriorMapVersionId])

  const onMissionNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempMissionName(e.target.value)
    setNameHasError(!e.target.value.trim())
  }

  const onCancelClicked = () => {
    setErrorMessage('')
    if (!actionInProgress) {
      clearTempApriorMapVersionId()
      setMode(MISSION_PAGE_MODES.VIEW_ZONES)
      setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
      if (!isEqual(tempVisualSettings, visualSettings)) {
        setTempVisualSettings({ ...visualSettings })
      }
    }
  }

  const onSaveClicked = async () => {
    setActionInProgress(true)
    setErrorMessage('')

    try {
      if (tempMissionName !== missionInfo?.name) {
        const mission = await MainApi.changeMissionName(missionId, tempMissionName)
        setMissionName(mission.name || '')
      }

      if (selectedApriorMapVersionId !== (missionInfo?.apriorMapVersionId ?? null)) {
        const mission = await MainApi.changeMissionApriorMapVersion(missionId, selectedApriorMapVersionId)
        setApriorMapVersionId(
          mission.aprior_map_version_id ? toApriorMapVersionId(mission.aprior_map_version_id) : null
        )
        clearTempApriorMapVersionId()
      }

      if (!isEqual(tempVisualSettings, visualSettings)) {
        const mission = await MainApi.changeMissionVisualSettings(missionId, tempVisualSettings)
        setVisualSettings({ ...DEFAULT_VIZUALIZATION_SETTINGS, ...mission.visualization_settings })
      }
    } catch (error) {
      if (error instanceof ApiError) {
        const details = error.body?.details
        const fieldErrors = details?.name || details?.aprior_map_version_id || details?.detail
        const fieldError = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors
        setErrorMessage(typeof fieldError === 'string' ? fieldError : t('mission_settings.save_error'))
      } else {
        // eslint-disable-next-line no-console
        console.error(error)
        setErrorMessage(t('mission_settings.save_error'))
      }
    } finally {
      setActionInProgress(false)
    }
  }

  const isSaveButtonDisabled =
    !tempMissionName.trim() ||
    (tempMissionName === missionInfo?.name &&
      selectedApriorMapVersionId === (missionInfo?.apriorMapVersionId ?? null) &&
      isEqual(visualSettings, tempVisualSettings))

  return (
    <div className={style.modal}>
      <div className={style.head}>
        <Typography variant="h5" className={style.title}>
          {t('mission_settings.title')}
        </Typography>
      </div>
      <div className={style.content}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('mission_settings.name_description')}
          error={t('mission_settings.name_error')}
          placeholder={t('mission_settings.name_placeholder')}
          hasError={hasNameError}
          defaultValue={missionInfo?.name}
          onChange={onMissionNameChanged}
        />

        <Box className={clsx('text-14-400', style.subtitle)} sx={{ mt: 2 }}>
          {t('mission_settings.map_name_description')}
        </Box>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <SelectForm
            value={selectedMap?.id?.toString() || ''}
            displayEmpty
            onChange={e => {
              if (!e.target.value) {
                setTempApriorMapVersionId(null)
                return
              }
              const map = maps.find(m => m.id === toApriorMapId(Number(e.target.value)))
              setTempApriorMapVersionId(
                map?.lastVersionId || map?.versions.find(v => v.status === APRIOR_MAP_STATUS.OK)?.id || null
              )
            }}
          >
            <MenuItem value="">{t('mission_settings.no_aprior_map')}</MenuItem>
            {maps
              .filter(m => m.versions.some(v => v.status === APRIOR_MAP_STATUS.OK))
              .map(m => (
                <MenuItem key={m.id} value={m.id.toString()}>
                  {m.name}
                </MenuItem>
              ))}
          </SelectForm>
          <SelectForm
            value={selectedApriorMapVersionId?.toString() || ''}
            onChange={e =>
              setTempApriorMapVersionId(e.target.value ? toApriorMapVersionId(Number(e.target.value)) : null)
            }
            disabled={!selectedMap}
          >
            {(selectedMap?.versions || [])
              .filter(v => v.status === APRIOR_MAP_STATUS.OK)
              .map((v: IApriorMapVersion) => (
                <MenuItem key={v.id} value={v.id.toString()}>
                  {v.name ? `${v.name} ` : ''}[{v.version}]
                </MenuItem>
              ))}
          </SelectForm>
        </Stack>

        <div className={style.contentColumns}>
          <TextFieldDescribed
            sxInput={{ width: '120px' }}
            description={t('mission_settings.buffer_description')}
            rightLabel={t('mission_settings.meters')}
            placeholder={t('mission_settings.name_placeholder')}
            defaultValue="25"
            onChange={() => {}}
          />
          <TextFieldDescribed
            sxInput={{ width: '120px' }}
            className={style.settingsMeters}
            description={t('mission_settings.start_point_max_distance')}
            rightLabel={t('mission_settings.meters')}
            placeholder={t('mission_settings.name_placeholder')}
            defaultValue="33"
            onChange={() => {}}
          />
        </div>
        <VisualSettings />
        <ScheduleSettings />
      </div>
      {errorMessage && (
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
          <div className={style.errorMessage}>{errorMessage}</div>
        </Stack>
      )}
      <div className={style.footer}>
        <Button
          onClick={onCancelClicked}
          disabled={actionInProgress}
          color="error"
          variant="contained"
          fullWidth
          endIcon={<CancelSvg />}
        >
          {t('mission_settings.label_cancel')}
        </Button>
        <Button
          disabled={isSaveButtonDisabled || actionInProgress}
          onClick={onSaveClicked}
          fullWidth
          variant="contained"
          color="primary"
          endIcon={actionInProgress ? <CircularProgress size={16} color="secondary" /> : <SaveSvg />}
        >
          {t('mission_settings.label_save')}
        </Button>
      </div>
    </div>
  )
}

export default MissionSettingsModal
